import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { merge, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { Employee } from 'src/app/employee/models/Employee';
import { EmployeeService } from 'src/app/employee/services/employee.service';
import { SubscriptionService } from 'src/app/setting/services/subscription.service';
import { ModuleNavigationRoute } from 'src/app/shared/models/ModuleNavigationRoute';
import { AppService } from 'src/app/shared/services/app.service';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { EntityUpdateRequest } from '../../../../entity_update_request/state/entity_update_request.model';
import { AvailableWidgets } from '../../../../setting/models/AvailableWidgets';
import { Role } from '../../../../setting/models/Role';
import { Subscription as SubscriptionModel } from '../../../../setting/models/Subscription';
import { CompanyService } from '../../../../setting/services/company.service';
import { RolesService } from '../../../../setting/services/roles.service';
import { SummernoteHelper } from '../../../../shared/common/SummernoteHelper';
import { WidgetTemplate } from '../../../../shared/models/WidgetTemplate';
import { WidgetTemplatesService } from '../../../../shared/services/widget-templates.service';
import { AvailableFeatures } from '../../../models/AvailableFeatures';
import { EmployeeRequiredDataUpdates } from '../../../models/EmployeeRequiredDataUpdates';
import { ChangeAvatarModalComponent } from '../../partials/change-avatar-modal/change-avatar-modal.component';
import { ChangeEntityActiveAccessModalComponent } from '../../partials/change-employee-active-access-modal/change-entity-active-access-modal.component';
import { ChangeEmployeeCompanyGroupModalComponent } from '../../partials/change-employee-company-group/change-employee-company-group-modal.component';
import { CreateEmployeeTerminationModalComponent } from '../../partials/create-employee-termination-modal/create-employee-termination-modal.component';
import { CreateEditEmployeeActivityModalComponent } from '../create-edit-employee-activity-modal/create-edit-employee-activity-modal.component';
import { CreateEditEmployeeModalComponent } from '../create-edit-employee-modal/create-edit-employee-modal.component';
import { EditEmployeeSupervisorComponent } from '../edit-employee-supervisor/edit-employee-supervisor.component';

interface ConfirmDialogOptions {
    cancelButtonText?: string;
    confirmButtonText: string;
    title: string;
    text: string;
    confirmButtonColor?: string;
    showCancelButton?: boolean;
}

const DEFAULT_CONFIRM_DIALOG_OPTIONS: Pick<ConfirmDialogOptions, 'confirmButtonColor' | 'showCancelButton'> = {
    confirmButtonColor: '#6979ED',
    showCancelButton: true
};

@Component({
    selector: 'app-employee-detail',
    templateUrl: './employee-detail.component.html',
    styleUrls: ['./employee-detail.component.css']
})
export class EmployeeDetailComponent implements OnInit, OnDestroy {
    @ViewChild('employeeTypeModal', {static: false})
    public employeeTypeModal: TemplateRef<ElementRef>;
    @ViewChild('requestEmployeeChangeDataModal', {static: false})
    public requestEmployeeChangeDataModal: TemplateRef<ElementRef>;

    public employee: Employee;
    public employee$: Observable<Employee>;
    public pageTitle$: Observable<string>;
    public availableFeatures$: Observable<AvailableFeatures>;
    public availableWidgets$: Observable<AvailableWidgets>;
    public availableRoutes: Array<ModuleNavigationRoute> = [];
    public watchingMyself: boolean;
    public loggedUserPermissions: Array<string>;
    public allRoutes: Array<ModuleNavigationRoute> = [
        {
            routerLink: 'overview',
            title: this._translateService.instant('employees.page_title_view_overview')
        },
        {
            routerLink: 'positions-files',
            title: this._translateService.instant('employees.page_title_view_position_structure')
        },
        {
            routerLink: 'documents',
            title: this._translateService.instant('employees.page_title_view_files_payrolls')
        },
        {
            routerLink: 'properties',
            title: this._translateService.instant('employees.page_title_view_properties_benefits')
        },
        {
            routerLink: 'evaluations',
            title: this._translateService.instant('employees.page_title_view_kpis'),
        },
        {
            routerLink: 'analysis',
            title: this._translateService.instant('employees.page_title_view_analysis')
        },
        {
            routerLink: 'notes',
            title: this._translateService.instant('employees.page_title_view_activity')
        },
        {
            routerLink: 'datetimes',
            title: this._translateService.instant('employees.page_title_view_datetimes')
        },
        {
            routerLink: 'setting',
            title: this._translateService.instant('employees.page_title_view_setting')
        },
    ];
    public canArchive = false;
    public canDelete = false;
    public canEdit = false;
    public canCreate = false;
    public canTerminate = false;
    public canUnarchive = false;
    public canViewPersonalData = false;
    public employeeTypes$: Observable<Array<string>>;
    public widgetTemplates$: Observable<Array<WidgetTemplate>>;
    public employeeRoles$: Observable<Array<Role>>;
    public profileLoaded = false;
    public isCollapsed = true;
    public submitted = false;
    public isAdmin = false;
    public integrationAbraActive = false;
    public employeeTypeForm: UntypedFormGroup;
    public requestEmployeeChangeDataForm: UntypedFormGroup;
    public subscription$ = this._subscriptionService.subscription$;
    public subscription: SubscriptionModel;
    public requiredUpdates: EmployeeRequiredDataUpdates;
    public entityRequests: Array<EntityUpdateRequest>;
    public fetchEmployeeExtended$ = new ReplaySubject<void>(1);
    public employeeID: number;

    private _subscriptions = new Subscription();
    private _employeeTypeModal: NgbModalRef;
    private _requestEmployeeChangeDataModal: NgbModalRef;

    public constructor(
        public summernoteHelper: SummernoteHelper,
        private _appService: AppService,
        private _authService: AuthenticationService,
        private _employeeService: EmployeeService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _translateService: TranslateService,
        private _subscriptionService: SubscriptionService,
        private _companyService: CompanyService,
        private _fb: UntypedFormBuilder,
        private _modalService: NgbModal,
        private _roleService: RolesService,
        private _widgetTemplateService: WidgetTemplatesService
    ) { }

    public get f(): { [formControlName: string]: AbstractControl; } {
        return this.employeeTypeForm.controls;
    }

    public ngOnDestroy(): void {
        this._subscriptions?.unsubscribe();
        this._appService.pageTitleObject.next({label: null, type: null});
    }

    public ngOnInit(): void {
        this.availableFeatures$ = this._subscriptionService.availableFeatures$;

        this.availableWidgets$ = this._companyService.availableWidgets$;

        this.loggedUserPermissions = this._authService?.permissions?.map(perm => perm.name);

        this.integrationAbraActive = this._authService.loggedUser.integrations.abra;

        this.subscription$.subscribe(subscription => this.subscription = subscription);

        this.employeeTypeForm = this._fb.group({
            type: [null, Validators.required],
            role: [null, Validators.required],
            is_active: [true, Validators.required]
        });

        this.requestEmployeeChangeDataForm = this._fb.group({
            widget_template_ID: [0, Validators.required],
            require_update_message: [''],
        });

        this.pageTitle$ = merge(
            this._router.events.pipe(filter(e => e instanceof NavigationEnd)),
            this._route.firstChild.data
        )
            .pipe(
                switchMap(() => merge(
                    this._route.data.pipe(map(data => data.title)),
                    this._route.firstChild.data.pipe(map(data => data.title)),
                    this._authService.pageTitle
                )),
                filter(title => title !== null)
            );

        // on employeeID in rout change
        this._subscriptions.add(this._route.params.subscribe(params => {
            this.employeeID = parseInt(params.id, 10);
            this.profileLoaded = false;
            this.fetchEmployeeExtended$.next();
        }));

        this._subscriptions.add(this.fetchEmployeeExtended$
            .pipe(
                switchMap(() => this._employeeService.getEmployeeInfoByEmployeeID(this.employeeID, 'extend|navigation|permissions:view', true)
                    .pipe(map(employee => employee))
                ),
                switchMap(employee => this.availableWidgets$
                    .pipe(map(availableWidgets => ({employee, availableWidgets})))
                ),
                switchMap(({employee, availableWidgets}) => this.availableFeatures$
                    .pipe(map(availableFeatures => ({employee, availableWidgets, availableFeatures})))
                ),
                map(({employee, availableWidgets, availableFeatures}) => {
                    const loggedEmployeeID = this._authService?.employee?.employee_ID;
                    this.watchingMyself = loggedEmployeeID === employee?.employee_ID;
                    this.availableRoutes = [];
                    this.employee = employee;
                    this.employee$ = of(employee);
                    this.profileLoaded = true;

                    this._setPermissions();

                    if (employee.employee_type === 'POTENTIAL') {
                        this.employeeTypeForm.controls.type?.setValue('COMMON');
                    } else {
                        this.employeeTypeForm.patchValue({type: employee.employee_type});
                    }

                    this.allRoutes.forEach(route => {
                        if (this.isMenuItemVisible(route, availableWidgets, availableFeatures)) {
                            this.availableRoutes.push(route);
                        }
                    });

                    return employee;
                }),
                shareReplay()
            ).subscribe(employee => {
                this._appService.pageTitleObject.next({label: employee.fullname, type: 'EMPLOYEE'});
            }));

        this.employeeRoles$ = this._roleService.getAllRoles()
            .pipe(
                map(roles => {
                    const employeeRoles = roles.filter(role => role.guard_type === 'employee');
                    const employeeRole = roles.find(role => role.internal_name === 'employee');
                    let presetRoleID = employeeRole?.role_ID;

                    if (this.changeToCommon() && this.employee.role_ID) {
                        presetRoleID = this.employee.role_ID;
                    }
                    this.employeeTypeForm.patchValue({role: presetRoleID});

                    return employeeRoles;
                })
            );

        this.widgetTemplates$ = this._widgetTemplateService.getAllTemplates('select')
            .pipe(map(templates => {
                templates.push({id: 0, name: this._translateService.instant('widget_template.no_template'), message: null});
                if (this._widgetTemplateService.defaultWidgetTemplate?.id) {
                    this.requestEmployeeChangeDataForm.patchValue({
                        widget_template_ID: this._widgetTemplateService.defaultWidgetTemplate.id,
                        require_update_message: this._widgetTemplateService.defaultWidgetTemplate.message
                    });
                }
                return templates;
            }));

        this.isAdmin = this._authService.loggedUser?.role_level === 1;

        // permissions

        this.employeeTypes$ = this._employeeService.getEmployeeTypes();
    }

    public isMenuItemVisible(route: ModuleNavigationRoute, availableWidgets: AvailableWidgets, availableFeatures: AvailableFeatures): boolean {
        const currentWidgets = Object.values(availableWidgets?.employee[route.routerLink]);

        // check if user has permission for more than 0 widgets for this route
        switch (route.routerLink) {
            case 'overview':
                return true;
            case 'documents':
                if (!this.checkPermissions(['employeeFile', 'employeePayroll'])) {
                    return false;
                }
                break;
            case 'properties':
                if (!this.checkPermissions(['benefit', 'employeeProperty'])) {
                    return false;
                }
                break;
            case 'evaluations':
                if (!this.checkPermissions(['kpi', 'growth', 'employeeReview'])) {
                    return false;
                }
                if (!(availableFeatures.growths || availableFeatures.evaluations)) {
                    return false;
                }
                break;
            case 'analysis':
                if (!this.checkPermissions(['analyse'])) {
                    return false;
                }
                if (!this.checkPermissions(['employeeSalary', 'employeeBonus', 'employeeDatetime', 'employeeReview'])) {
                    return false;
                }
                break;
            case 'notes':
                if (!this.checkPermissions(['employeeActivity'])) {
                    return false;
                }
                break;
            case 'datetimes':
                if (!this.checkPermissions(['companyEmployeePolicy', 'employeePolicyAdjustement', 'employeeDatetime'])) {
                    return false;
                }
                break;
            case 'setting':
                if (!this.checkPermissions(['employee', 'security'], 'edit')) {
                    return false;
                }
                break;
        }

        // check if more than 0 widgets is available for this route
        for (const item of currentWidgets) {
            if (item) {
                return true;
            }
        }

        return false;
    }

    public checkPermissions(permissions: Array<string>, action = 'view'): boolean {
        if (this.watchingMyself) {
            return true;
        }

        for (const perm of permissions) {
            if (this.loggedUserPermissions.includes(perm + '.' + action) || this.loggedUserPermissions.includes(perm + '.' + action + 'Structure')) {
                return true;
            }
        }

        return false;
    }

    public navigateToEntity(id: number): void {
        this._route.params.subscribe(params => {
            const oldID = params.id;
            void this._router.navigateByUrl(this._router.url.replace(oldID, id.toString()));
        });
    }

    public archiveEmployee(): void {
        if (this.employee.owned_company_groups.length > 0) {
            this._showConfirmDialog({
                showCancelButton: false,
                confirmButtonText: this._translateService.instant('swal.action_ok'),
                text: this._translateService.instant('employees.cannot_archive_owner_text'),
                title: this._translateService.instant('employees.action_archive')
            }).then(() => { });
            return;
        }
        if (this.employee.is_supervisor) {
            this._showConfirmDialog({
                showCancelButton: false,
                confirmButtonText: this._translateService.instant('swal.action_ok'),
                text: this._translateService.instant('employees.cannot_archive_supervisor_text'),
                title: this._translateService.instant('employees.action_archive')
            }).then(() => { });
            return;
        }

        const text = this.subscription.subscription.per_seats_pricing ? 'employees.action_archive_per_seat_text' : 'employees.action_archive_text';
        this._showConfirmDialog({
            cancelButtonText: this._translateService.instant('swal.action_cancel_delete'),
            confirmButtonText: this._translateService.instant('swal.action_confirm_delete'),
            text: this._translateService.instant(text),
            title: this._translateService.instant('employees.action_archive')
        })
            .then(result => {
                if (result.value) {
                    this._employeeService.archiveEmployee(this.employee.employee_ID)
                        .then(
                            () => this.fetchEmployeeExtended$.next(),
                            () => {
                            }
                        );
                }
            });
    }

    public deleteEmployee(): void {
        if (this.employee.owned_company_groups.length > 0) {
            this._showConfirmDialog({
                showCancelButton: false,
                confirmButtonText: this._translateService.instant('swal.action_ok'),
                text: this._translateService.instant('employees.cannot_delete_owner_text'),
                title: this._translateService.instant('employees.action_delete')
            }).then(() => { });
            return;
        }
        if (this.employee.is_supervisor) {
            this._showConfirmDialog({
                showCancelButton: false,
                confirmButtonText: this._translateService.instant('swal.action_ok'),
                text: this._translateService.instant('employees.cannot_delete_supervisor_text'),
                title: this._translateService.instant('employees.action_delete')
            }).then(() => { });
            return;
        }

        const text = this.subscription.subscription.per_seats_pricing ? 'employees.action_delete_per_seat_text' : 'employees.action_delete_text';
        this._showConfirmDialog({
            cancelButtonText: this._translateService.instant('swal.action_cancel_delete'),
            confirmButtonText: this._translateService.instant('swal.action_confirm_delete'),
            text: this._translateService.instant(text),
            title: this._translateService.instant('employees.action_delete')
        })
            .then(result => {
                if (result.value) {
                    this._employeeService.deleteEmployee(this.employee.employee_ID)
                        .then(
                            () => this._router.navigate(['/employee', 'list']),
                            () => this.fetchEmployeeExtended$.next()
                        );
                }
            });
    }

    public onEmployeeTypeSubmit(): void {
        let form = {type: this.employeeTypeForm.value.type};
        if (this.changeToCommon()) {
            form = this.employeeTypeForm.value;
            if (this.employeeTypeForm.invalid) {
                this.submitted = true;
                return;
            }
            this.submitted = false;
        }
        this._employeeService.saveEmployeeType(form, this.employee.employee_ID)
            .then(
                () => {
                    this._modalService.dismissAll();
                    this.refetchEmployee();
                },
                () => {
                }
            );
    }

    public onRequestEmployeeToUpdateDataSubmit(): void {
        const value = this.requestEmployeeChangeDataForm.value;
        if (value.widget_template_ID === 0) {
            value.widget_template_ID = null;
        }
        this._employeeService.forceEmployeeToUpdateData(this.requestEmployeeChangeDataForm.value, this.employee.employee_ID)
            .then(
                () => this._modalService.dismissAll(),
                () => {
                }
            );
    }

    public isButtonDisabled(): boolean {
        return this.requestEmployeeChangeDataForm.invalid;
    }

    public openAvatarChangeModal(): void {
        const modalRef = this._modalService.open(ChangeAvatarModalComponent, {centered: true});

        modalRef.componentInstance.employee = this.employee;
        modalRef.componentInstance.selectedAvatarUrl = this.employee.avatar;

        modalRef.result
            .then(
                () => this.fetchEmployeeExtended$.next(),
                () => {
                }
            );
    }

    public openEmployeeEditModal(): void {
        const modalRef = this._modalService.open(CreateEditEmployeeModalComponent, {centered: true});

        let updateRequested = false;
        if (this.entityRequests) {
            for (const request of this.entityRequests) {
                if (request.entity_type === 'App\\Models\\Employee') {
                    updateRequested = true;
                    break;
                }
            }
        }

        modalRef.componentInstance.employee = this.employee;
        modalRef.componentInstance.updateRequested = updateRequested;

        modalRef.result
            .then(
                () => this.refetchEmployee(),
                () => {
                }
            );
    }

    public openEmployeeTypeModal(): void {
        this._employeeTypeModal = this._modalService.open(this.employeeTypeModal);
    }

    public changeToCommon(): boolean {
        return this.employeeTypeForm.value.type === 'COMMON';
    }

    public openRequestEmployeeChangeDataModal(): void {
        this._requestEmployeeChangeDataModal = this._modalService.open(this.requestEmployeeChangeDataModal);
    }

    public openTerminationModal(employee: Employee): void {
        const modalRef = this._modalService.open(CreateEmployeeTerminationModalComponent, {centered: true});

        modalRef.componentInstance.employee = employee;

        modalRef.result
            .then(
                () => this.fetchEmployeeExtended$.next(),
                () => {
                }
            );
    }

    public unarchiveEmployee(): void {
        const text = this.subscription.subscription.per_seats_pricing ? 'employees.action_remove_from_archive_per_seat_text' : 'employees.action_remove_from_archive_text';
        this._showConfirmDialog({
            cancelButtonText: this._translateService.instant('swal.action_cancel_delete'),
            confirmButtonText: this._translateService.instant('swal.action_confirm_delete'),
            text: this._translateService.instant(text),
            title: this._translateService.instant('employees.action_remove_from_archive')
        })
            .then(result => {
                if (result.value) {
                    this._employeeService.unArchiveEmployee(this.employee.employee_ID)
                        .then(
                            () => this.fetchEmployeeExtended$.next(),
                            () => {
                            }
                        );
                }
            });
    }

    public openEmployeeSupervisorModal(): void {
        const modalRef = this._modalService.open(EditEmployeeSupervisorComponent, {centered: true});

        modalRef.componentInstance.employee = this.employee;

        modalRef.result
            .then(
                () => this.refetchEmployee(),
                () => {
                }
            );
    }

    public openEmployeeChangeCompanyGroupModal(): void {
        const modalRef = this._modalService.open(ChangeEmployeeCompanyGroupModalComponent, {centered: true});

        modalRef.componentInstance.employee = this.employee;

        modalRef.result
            .then(
                () => this.refetchEmployee(),
                () => {}
            );
    }

    public openEmployeeActiveAccessModal(): void {
        const modalRef = this._modalService.open(ChangeEntityActiveAccessModalComponent, {centered: true});

        modalRef.componentInstance.employee = this.employee;

        modalRef.result
            .then(
                () => this.refetchEmployee(),
                () => {}
            );
    }

    public openActivityModal(): void {
        const modalRef = this._modalService.open(CreateEditEmployeeActivityModalComponent, {centered: true});

        modalRef.componentInstance.employeeID = this.employee.employee_ID;
    }

    public loginAsThisEmployee(employee: Employee): void {
        this._authService.loginAsSpecificEmployee(employee.employee_ID);
    }

    public isTerminatedInPast(): boolean {
        return this.employee?.permissions?.createTermination && this.employee.termination?.terminated && new Date(this.employee.termination?.termination_end_date) < new Date();
    }

    public downloadProfilePdf(employeeId: number, employeeName: string): void {
        this._employeeService.downloadProfilePdf(employeeId, employeeName);
    }

    public widgetTemplateChanged($event: WidgetTemplate): void {
        this.requestEmployeeChangeDataForm.patchValue({require_update_message: $event.message});
    }

    public refetchEmployee(): void {
        this.fetchEmployeeExtended$.next();
    }

    public syncToAbra() {
        void this._employeeService.syncToAbra(this.employeeID);
    }

    private _showConfirmDialog(options: ConfirmDialogOptions): Promise<SweetAlertResult> {
        return Swal.fire({
            ...DEFAULT_CONFIRM_DIALOG_OPTIONS,
            ...options
        });
    }

    private _setPermissions(): void {
        this.canTerminate = this.employee?.permissions?.createTermination && !this.employee?.is_archived;
        this.canViewPersonalData = this.employee.permissions?.viewPersonalData || this.employee?.permissions?.editPersonalDataWithApproval;
        this.canArchive = this.employee?.permissions?.createTermination && !this.employee?.is_archived;
        this.canDelete = this.employee?.permissions?.delete;
        this.canEdit = this._authService.checkPermission(['employee.edit', 'employee.editStructure']);
        this.canUnarchive = this.employee.permissions?.createTermination && this.employee?.is_archived;
        this.canCreate = this._authService.checkPermission('scheduledTask.create');
    }
}
